import { getAbsoluteUrl } from 'helpers/url';
import React from 'react';
import Helmet from 'react-helmet';

const REDIRECT_STATUS_MAP = {
  moved_permanently: 301,
  permanent_redirect: 308
} as const;

type RedirectStatusKey = keyof typeof REDIRECT_STATUS_MAP;
export type RedirectStatusCode =
  (typeof REDIRECT_STATUS_MAP)[RedirectStatusKey];
type RedirectStatus = RedirectStatusKey | RedirectStatusCode;

/**
 * PrerenderMetaRedirect sets meta tags for prerendering services to perform HTTP redirects.
 *
 * @param props.to - The target URL to redirect to.
 * @param props.status - The redirect status, either as a code (301, 308) or a key ('moved_permanently', 'permanent_redirect').
 *
 * It injects the appropriate meta tags using react-helmet.
 */
const PrerenderMetaRedirect: React.FC<{
  to: string;
  status: RedirectStatus;
}> = ({ to, status }) => {
  const absoluteUrl = getAbsoluteUrl(to);
  const statusCode: RedirectStatusCode =
    typeof status === 'string' ? REDIRECT_STATUS_MAP[status] : status;
  return (
    <Helmet>
      <meta name="prerender-status-code" content={statusCode.toString()} />
      <meta name="prerender-header" content={`Location: ${absoluteUrl}`} />
    </Helmet>
  );
};

export default PrerenderMetaRedirect;
